import { create } from 'zustand';

const API_URL = (import.meta.env.VITE_STRIPE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** A partir de aquí se avisa al cliente de que quedan pocas unidades. */
export const LOW_STOCK_THRESHOLD = 5;

interface StockState {
  units: Record<string, number>;
  /** false mientras el backend no lleve existencias, o si no hay backend configurado. */
  tracked: boolean;
  status: 'idle' | 'loading' | 'ready' | 'error';
  load: () => void;
}

export const useStockStore = create<StockState>((set, get) => ({
  units: {},
  tracked: false,
  status: 'idle',
  load: () => {
    // Una sola petición por sesión: el stock se consulta al entrar, no en cada tarjeta.
    if (get().status !== 'idle' || !API_URL) return;
    set({ status: 'loading' });

    fetch(`${API_URL}/stock`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('stock no disponible'))))
      .then((body: { tracked?: boolean; stock?: Record<string, number> }) => {
        set({ units: body?.stock ?? {}, tracked: body?.tracked === true, status: 'ready' });
      })
      .catch(() => {
        // Si el backend no responde no se bloquea la tienda: simplemente no se muestra
        // disponibilidad. El checkout sigue siendo quien decide de verdad.
        set({ status: 'error', tracked: false });
      });
  },
}));

export type StockLevel =
  | { state: 'unknown' }
  | { state: 'out' }
  | { state: 'low'; units: number; label: string }
  | { state: 'ok'; units: number };

/** Traduce las unidades disponibles a lo que hay que enseñar en pantalla. */
export function readStockLevel(tracked: boolean, units: number | undefined): StockLevel {
  if (!tracked || units === undefined || !Number.isFinite(units)) return { state: 'unknown' };
  if (units <= 0) return { state: 'out' };
  if (units <= LOW_STOCK_THRESHOLD) {
    return {
      state: 'low',
      units,
      label: units === 1 ? 'Última unidad' : `Últimas ${units} unidades`,
    };
  }
  return { state: 'ok', units };
}

/** Nivel de existencias de un producto concreto, para usar dentro de un componente. */
export function useStockLevel(productId: string): StockLevel {
  const tracked = useStockStore((s) => s.tracked);
  const units = useStockStore((s) => s.units[productId]);
  return readStockLevel(tracked, units);
}
